import { useEffect, useRef, useState } from 'react';
import type { StatusLevel } from './StatusPill';

export type CustomFaultMarker = {
  id: string;
  label: string;
  hz: number;
  tone: 'critical' | 'warn' | 'watch' | 'info';
};

/**
 * Bearing catalog entry. Real implementation will load a JSON
 * dataset (SKF / NSK / Timken bearing geometries) and compute
 * BPFI/BPFO/BSF/FTF from rolling-element count + ball diameter +
 * pitch diameter + contact angle × shaft RPM. The demo carries a
 * handful of common motor bearings so the picker isn't empty.
 */
export type BearingCatalogEntry = {
  partNumber: string;
  manufacturer: string;
  /** Number of rolling elements. */
  n: number;
  /** BPFI multiplier (× shaft RPM). Pre-computed for the demo. */
  bpfiMul: number;
  bpfoMul: number;
  bsfMul: number;
  ftfMul: number;
};

export const BEARING_CATALOG: BearingCatalogEntry[] = [
  { partNumber: '6205-2RS', manufacturer: 'SKF',    n: 9,  bpfiMul: 5.43, bpfoMul: 3.57, bsfMul: 2.36, ftfMul: 0.39 },
  { partNumber: '6204',     manufacturer: 'SKF',    n: 8,  bpfiMul: 4.94, bpfoMul: 3.06, bsfMul: 2.06, ftfMul: 0.38 },
  { partNumber: '6308',     manufacturer: 'SKF',    n: 8,  bpfiMul: 4.92, bpfoMul: 3.08, bsfMul: 2.04, ftfMul: 0.38 },
  { partNumber: '6204',     manufacturer: 'NSK',    n: 8,  bpfiMul: 4.93, bpfoMul: 3.07, bsfMul: 2.05, ftfMul: 0.38 },
  { partNumber: '22220',    manufacturer: 'Timken', n: 16, bpfiMul: 9.43, bpfoMul: 6.57, bsfMul: 3.61, ftfMul: 0.41 },
];

const TONES: CustomFaultMarker['tone'][] = ['info', 'watch', 'warn', 'critical'];

export type FaultFrequencyEditorProps = {
  open: boolean;
  onClose: () => void;
  /** Asset id the editor is scoped to. */
  assetId: string;
  /** Asset class label for the heading. */
  assetName: string;
  /** Shaft RPM so the bearing-catalog picker can compute Hz on the fly. */
  shaftRpm: number;
  /** Current custom markers. */
  markers: CustomFaultMarker[];
  /** Commit a new marker list (replace all). */
  onSave: (markers: CustomFaultMarker[]) => void;
};

const TONE_BG: Record<CustomFaultMarker['tone'], string> = {
  critical: 'bg-critical text-gray-50',
  warn: 'bg-warn text-gray-900',
  watch: 'bg-watch text-gray-900',
  info: 'bg-info text-gray-50',
};

/**
 * Modal editor for per-asset fault frequencies. Lets the operator:
 *   - Add markers from the bearing catalog (auto-computes BPFI/BPFO/
 *     BSF/FTF from the bearing geometry × shaftRpm).
 *   - Add free-form markers (label + Hz + severity tone).
 *   - Remove markers.
 *
 * The parent owns persistence — typically localStorage keyed by
 * `pdm.fault-markers.{assetId}` until the asset-service ships the
 * fault-frequency endpoint.
 */
export function FaultFrequencyEditor({
  open,
  onClose,
  assetName,
  shaftRpm,
  markers,
  onSave,
}: FaultFrequencyEditorProps) {
  const [draft, setDraft] = useState<CustomFaultMarker[]>(markers);
  const [bearing, setBearing] = useState<string>(BEARING_CATALOG[0].partNumber);
  const [customLabel, setCustomLabel] = useState('');
  const [customHz, setCustomHz] = useState('');
  const [customTone, setCustomTone] = useState<CustomFaultMarker['tone']>('warn');
  const dialogRef = useRef<HTMLDivElement>(null);

  // Sync the in-modal draft with the parent's markers whenever the
  // modal opens (so a stale Cancel doesn't carry edits across opens).
  useEffect(() => {
    if (open) setDraft(markers);
  }, [open, markers]);

  // Esc closes; focus-trap is deliberately omitted for the demo
  // surface (single modal with a small focus set). Real prod modal
  // should use a focus-trap library.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const selectedBearing = BEARING_CATALOG.find((b) => `${b.manufacturer} ${b.partNumber}` === bearing) ?? BEARING_CATALOG[0];

  function addFromCatalog() {
    const rps = shaftRpm / 60;
    const items: CustomFaultMarker[] = [
      { id: rid(), label: 'BPFI', hz: round(selectedBearing.bpfiMul * rps), tone: 'critical' },
      { id: rid(), label: 'BPFO', hz: round(selectedBearing.bpfoMul * rps), tone: 'warn' },
      { id: rid(), label: 'BSF', hz: round(selectedBearing.bsfMul * rps), tone: 'watch' },
      { id: rid(), label: 'FTF', hz: round(selectedBearing.ftfMul * rps), tone: 'info' },
    ];
    setDraft([...draft, ...items]);
  }

  function addCustom() {
    const hz = parseFloat(customHz);
    if (!customLabel || !Number.isFinite(hz)) return;
    setDraft([...draft, { id: rid(), label: customLabel, hz: round(hz), tone: customTone }]);
    setCustomLabel('');
    setCustomHz('');
  }

  function remove(id: string) {
    setDraft(draft.filter((m) => m.id !== id));
  }

  function save() {
    onSave(draft);
    onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fault-editor-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/30 p-sp-6"
    >
      <div
        ref={dialogRef}
        className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-r-card border border-gray-300 bg-gray-50 shadow-lg"
      >
        <header className="flex items-start justify-between border-b border-gray-300 p-sp-4">
          <div>
            <h2 id="fault-editor-title" className="text-fs-statement font-medium text-gray-900">
              Fault frequencies · {assetName}
            </h2>
            <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
              Shaft RPM: {shaftRpm} · used to compute Hz from bearing geometry
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-r-chip px-sp-2 py-sp-1 text-fs-meta text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
            aria-label="Close fault frequency editor"
          >
            ✕
          </button>
        </header>

        <div className="flex flex-col gap-sp-4 overflow-y-auto p-sp-4">
          <section aria-labelledby="catalog-title" className="flex flex-col gap-sp-2">
            <h3 id="catalog-title" className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              From bearing catalog
            </h3>
            <div className="flex flex-wrap items-center gap-sp-2">
              <label className="text-fs-meta text-gray-700">
                Bearing{' '}
                <select
                  value={bearing}
                  onChange={(e) => setBearing(e.target.value)}
                  className="ml-sp-1 rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 font-mono text-fs-meta text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
                >
                  {BEARING_CATALOG.map((b) => {
                    const key = `${b.manufacturer} ${b.partNumber}`;
                    return (
                      <option key={key} value={key}>
                        {b.manufacturer} {b.partNumber} · {b.n} balls
                      </option>
                    );
                  })}
                </select>
              </label>
              <button
                type="button"
                onClick={addFromCatalog}
                className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
              >
                Add BPFI/BPFO/BSF/FTF
              </button>
            </div>
          </section>

          <section aria-labelledby="custom-title" className="flex flex-col gap-sp-2">
            <h3 id="custom-title" className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Custom marker
            </h3>
            <div className="flex flex-wrap items-center gap-sp-2">
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="Label (e.g. GMF, BPF)"
                className="w-40 rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-meta text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
              />
              <input
                type="number"
                step="0.1"
                value={customHz}
                onChange={(e) => setCustomHz(e.target.value)}
                placeholder="Hz"
                className="w-24 rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-right font-mono text-fs-meta text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
              />
              <select
                value={customTone}
                onChange={(e) => setCustomTone(e.target.value as CustomFaultMarker['tone'])}
                className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-meta text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
              >
                {TONES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={addCustom}
                disabled={!customLabel || !customHz}
                className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </section>

          <section aria-labelledby="current-title" className="flex flex-col gap-sp-2">
            <h3 id="current-title" className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Markers ({draft.length})
            </h3>
            {draft.length === 0 ? (
              <p className="text-fs-meta text-gray-700">No markers yet.</p>
            ) : (
              <ul className="flex flex-col gap-sp-1">
                {draft.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center justify-between rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
                  >
                    <div className="flex items-center gap-sp-2">
                      <span className={`inline-flex h-sp-2 w-sp-2 rounded-r-chip ${TONE_BG[m.tone]}`} aria-hidden />
                      <span className="font-medium text-gray-900">{m.label}</span>
                      <span className="font-mono text-fs-meta text-gray-700">{m.hz} Hz</span>
                      <span
                        className={`ml-sp-1 rounded-r-chip px-sp-2 py-sp-1 text-fs-meta ${TONE_BG[m.tone]}`}
                      >
                        {m.tone}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(m.id)}
                      className="rounded-r-chip px-sp-2 py-sp-1 text-fs-meta text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
                      aria-label={`Remove ${m.label}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <footer className="flex items-center justify-end gap-sp-2 border-t border-gray-300 p-sp-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2 text-fs-meta text-gray-900 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            className="rounded-r-default bg-gray-900 px-sp-3 py-sp-2 text-fs-meta font-medium text-gray-50 hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
          >
            Save markers
          </button>
        </footer>
      </div>
    </div>
  );
}

function rid(): string {
  return `m_${Math.random().toString(36).slice(2, 9)}`;
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Local-storage-backed persistence for custom markers. Keyed per
 * asset so two assets don't share markers. Future: swap for
 * `/v1/assets/:id/fault-markers` once the endpoint exists.
 */
export function loadCustomMarkers(assetId: string): CustomFaultMarker[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(assetId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isMarker);
  } catch {
    return [];
  }
}

export function saveCustomMarkers(assetId: string, markers: CustomFaultMarker[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(assetId), JSON.stringify(markers));
  } catch {
    /* localStorage may be disabled; the editor still works in-memory */
  }
}

function storageKey(assetId: string): string {
  return `pdm.fault-markers.${assetId}`;
}

function isMarker(x: unknown): x is CustomFaultMarker {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.label === 'string' &&
    typeof o.hz === 'number' &&
    typeof o.tone === 'string' &&
    (o.tone === 'critical' || o.tone === 'warn' || o.tone === 'watch' || o.tone === 'info')
  );
}

// `StatusLevel` is widely used; the editor's tone subset doesn't
// include 'healthy' (a marker isn't a status), but we surface the
// import so consumers can map across surfaces with one type.
export type _AlignsWithStatusLevel = StatusLevel;
